import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CSpinner,
  CStepper,
} from "@coreui/react-pro";
import {
  canResumeSubscription,
  deriveSubscriptionDisplayStatus,
  isStaffManaged,
  useMySubscriptions,
  useResumeSubscription,
} from "../../../api/hooks/useStrategySubscriptions";
import type { ApiError } from "../../../api/client";
import { useFailure } from "../../../context/useFailure";
import { useToast } from "../../../context/useToast";
import { routePaths } from "../../../routes/route-paths";
import { StrategyPriceText } from "../components/StrategyPrice";
import { formatDateShort } from "../../../lib/dates";
import { StrategySideLabel } from "../components/StrategySideLabel";

// CStepper's exposed imperative API. Mirrors the End wizard so the two flows stay aligned.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

export function SubscriptionResumeWizardCoreui() {
  const navigate = useNavigate();
  const { strategyId = "" } = useParams<{ strategyId: string }>();

  const { data: subscriptions = [], isLoading, error } = useMySubscriptions();
  const resumeRemote = useResumeSubscription();
  const { showSuccess } = useToast();
  const { showFailure, clearFailure } = useFailure();

  const stepperRef = useRef<CStepperRef>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Only a cancellation that has not yet taken effect can be resumed — the same rule the server
  // enforces. Anything else lands on the "nothing to resume" message rather than a dead flow.
  const subscription = subscriptions.find(
    (s) =>
      s.strategy.id === strategyId &&
      !isStaffManaged(s) &&
      canResumeSubscription(s),
  );

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <CAlert color="danger">
          Unable to load your subscription. Please try again.
        </CAlert>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div>
        <CAlert color="warning">
          There's no cancelled subscription to resume for this strategy.{" "}
          <Link to={routePaths.myStrategies}>Back to my strategies</Link>.
        </CAlert>
      </div>
    );
  }

  // Cancel on the first step takes the customer back to the subscription detail
  // page they came from, matching the End wizard's return behaviour.
  const detailPath = `/subscriptions/${strategyId}`;

  const handleNext = () => stepperRef.current?.next();

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(detailPath);
    } else {
      stepperRef.current?.prev();
    }
  };

  // The submit step resumes the subscription, surfaces a toast, and routes back
  // to the detail page where it now reads as Active again.
  const handleSubmit = async () => {
    setSubmitting(true);
    clearFailure();
    try {
      await resumeRemote.mutateAsync(strategyId);
      showSuccess(`Subscription to ${subscription.strategy.publicName} resumed.`);
      navigate(detailPath);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Unable to resume this subscription. Please try again.";
      showFailure(message, (err as ApiError)?.traceId);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      label: "Details",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Details</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">
              You're about to resume the following subscription. Review the
              details, then continue to confirm.
            </p>
            <dl className="row mb-4">
              <dt className="col-bo-3 text-body-secondary fw-normal">
                Strategy
              </dt>
              <dd className="col-bo-9 fw-semibold">
                {subscription.strategy.publicName}
                <div className="small fw-normal mt-1">
                  <StrategySideLabel direction={subscription.strategy.direction} />
                </div>
              </dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">Status</dt>
              <dd className="col-bo-9">
                {deriveSubscriptionDisplayStatus(subscription)}
              </dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">
                Access until
              </dt>
              <dd className="col-bo-9">
                {subscription.activeTo
                  ? formatDateShort(subscription.activeTo)
                  : "Open-ended"}
              </dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">
                Billing
              </dt>
              <dd className="col-bo-9">
                <StrategyPriceText
                  free={subscription.priceAtSubscriptionUsd === 0}
                  amountUsd={subscription.priceAtSubscriptionUsd}
                  cycle={subscription.billingCycle}
                />
              </dd>
            </dl>
            <hr className="mt-0 mb-4" />
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={handleBack}>
                Cancel
              </CButton>
              <CButton color="primary" onClick={handleNext}>
                Next: Review
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Review",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Review</h4>
          </CCardHeader>
          <CCardBody>
            <CAlert color="success">
              This subscription was cancelled
              {subscription.activeTo
                ? `, with access set to end on ${formatDateShort(subscription.activeTo)}`
                : ""}
              . Resuming reverses that.
            </CAlert>
            <p>When you confirm:</p>
            <ul className="mb-4">
              <li>
                Your subscription to{" "}
                <strong>{subscription.strategy.publicName}</strong> continues with no
                end date.
              </li>
              <li>
                Your access to the strategy continues uninterrupted.
              </li>
              <li>
                Billing continues on your normal cycle (
                <StrategyPriceText
                  free={subscription.priceAtSubscriptionUsd === 0}
                  amountUsd={subscription.priceAtSubscriptionUsd}
                  cycle={subscription.billingCycle}
                />
                ), before any offer.
              </li>
              {/* Said rather than shown: while a line is cancelled its offer takes nothing off the
                  next bill, so there is no figure here to name it by. What matters is the reassurance
                  — the offer is kept for exactly this case. */}
              <li>
                Any offer running on this strategy comes back with it, with the time it had
                left. <Link to={routePaths.billing}>Billing</Link> shows what you'll actually pay.
              </li>
            </ul>
            <hr className="mt-0 mb-4" />
            <div className="d-flex justify-content-between">
              <CButton
                color="secondary"
                variant="outline"
                onClick={handleBack}
                disabled={submitting}
              >
                Back
              </CButton>
              <CButton
                color="success"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting && <CSpinner size="sm" className="me-2" />}
                Resume subscription
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>Resume subscription</h2>
      <p className="text-body-secondary">
        Continue your subscription to{" "}
        <strong>{subscription.strategy.publicName}</strong>.
      </p>
      <hr />

      <CStepper
        ref={stepperRef}
        steps={steps}
        onStepChange={setCurrentStep}
        onReset={() => setCurrentStep(1)}
      />
    </>
  );
}
